import * as React from 'react';
import { get } from 'lodash';

import { ColumnType } from '@inqool/uas-web-core/es/components/controls/Grid/constants';

import {
  eventHistoryActionEnum,
  eventHistoryCategoryEnum,
  eventHistorySourceEnum,
  identificationTypeEnum,
  participantsClassificationTypeEnum,
  participantsTypeEnum,
  stateEnum
} from '@modules/events/_eventsEnums';
import { maxTextLength } from '@utils/utils';
import { withProps } from 'recompose';
import { EventsAudioRecordDownload } from './EventsAudioRecordDownload';
import { TextField } from 'material-ui';

export const eventsGridColumns = [
  { label: 'Spisová značka', field: 'recordSymbol', type: ColumnType.NUMBER, width: 80 },
  { label: 'Číslo', field: 'number', type: ColumnType.TEXT, width: 40 },
  { label: 'Označení', field: 'customId', type: ColumnType.TEXT, width: 80 },
  {
    label: 'Čas oznámení',
    field: 'report.datetime',
    displayField: 'report',
    mapper: parent => get(parent, 'datetime'),
    type: ColumnType.DATETIME,
    width: 100,
    compact: true
  },
  {
    label: 'Typ',
    field: 'type.name',
    displayField: 'type',
    mapper: parent => get(parent, 'name'),
    type: ColumnType.TEXT,
    width: 80
  },
  {
    label: 'Stav',
    field: 'state.id',
    displayField: 'state',
    type: ColumnType.ENUM,
    enum: stateEnum,
    width: 120,
    align: 'left'
  },
  {
    label: 'Uzamknuta Událost',
    field: 'lock',
    type: ColumnType.BOOLEAN,
    width: 60,
    align: 'left'
  },
  {
    label: 'Přestupce',
    field: 'participants.fullName',
    displayField: 'participants',
    mapper: o =>
      o
        .filter(i => i.type === 'PERPETRATOR')
        .slice(0, 3)
        .map(i => i.person.fullName)
        .join(', '),
    type: ColumnType.TEXT,
    width: 120
  },
  {
    label: 'Vozidlo',
    field: 'vehicles.registrationNumber',
    displayField: 'vehicles',
    mapper: o =>
      o
        .slice(0, 3)
        .map(i => i.registrationNumber)
        .join(', '),
    type: ColumnType.TEXT,
    width: 50
  },
  {
    label: 'Způsob řešení události',
    field: 'resolution.name',
    displayField: 'resolution',
    mapper: parent => get(parent, 'name'),
    type: ColumnType.TEXT,
    width: 80
  },
  {
    label: 'Adresa',
    field: 'address',
    mapper: parent => get(parent, 'fullAddress'),
    type: ColumnType.TEXT,
    width: 120
  },
  {
    label: 'Obec',
    field: 'addressObject.municipality',
    displayField: 'address',
    mapper: parent => get(parent, 'municipality'),
    type: ColumnType.TEXT,
    width: 80
  },
  {
    label: 'Část obce',
    field: 'addressObject.municipalityPart',
    displayField: 'address',
    mapper: parent => get(parent, 'municipalityPart'),
    type: ColumnType.TEXT,
    width: 80
  },
  { label: 'Čas události', field: 'eventDate', type: ColumnType.DATETIME, width: 100, compact: true },
  { label: 'Poznámka', field: 'note', type: ColumnType.TEXT, mapper: o => maxTextLength(o, 20), width: 120 },
  {
    label: 'Vytvořil',
    field: 'author.name',
    displayField: 'author',
    type: ColumnType.TEXT,
    width: 120,
    align: 'left',
    mapper: o => get(o, 'fullName')
  },
  {
    label: 'Pracoviště',
    field: 'station.name',
    displayField: 'station',
    type: ColumnType.TEXT,
    mapper: parent => get(parent, 'name'),
    width: 120
  },
  {
    label: 'Směna',
    field: 'shift.name',
    displayField: 'shift',
    type: ColumnType.TEXT,
    mapper: parent => get(parent, 'name'),
    width: 120
  },
  {
    label: 'Klasifikace',
    field: 'classifications',
    mapper: (cs = []) => cs.map(c => get(c, 'name')).join(', '),
    type: ColumnType.TEXT,
    width: 120
  },
  {
    label: 'Popis',
    field: 'description',
    type: ColumnType.JSX,
    width: 240,
    clickable: false,
    filterComponent: ({ id, name, onChange, ...rest }) => {
      return <TextField id={id} name={name} onChange={(_e, v) => onChange(v)} {...rest} />;
    },
    filterOperations: ['CONTAINS'],
    jsx: ({ row }) => {
      const description = get(row, 'description');
      if (description) {
        const descriptionJson = JSON.parse(description);
        if (descriptionJson && descriptionJson.blocks) {
          const blocks = descriptionJson.blocks.map(b => b.text);
          const reducedBlocks: any[] = [];
          let emptyCount = 0;
          blocks.forEach(item => {
            if (item === '') {
              emptyCount++;
              if (emptyCount === 1) {
                reducedBlocks.push(item);
              }
            } else {
              emptyCount = 0;
              reducedBlocks.push(item);
            }
          });
          return (
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              title={reducedBlocks.join('\n')}
            >
              <div style={{ textAlign: 'left', flexGrow: 1 }}>{maxTextLength(reducedBlocks.join(' '), 60)}</div>
            </div>
          );
        }
      }
      return <div />;
    }
  },
  {
    label: 'Vlastní místo',
    field: 'place.name',
    displayField: 'place',
    type: ColumnType.TEXT,
    mapper: parent => get(parent, 'name'),
    width: 120
  }
];

// prettier-ignore
export const eventsFormParticipantsGridColumns = [
  { label: 'Jméno', field: 'person.firstName', displayField: 'person', mapper: o => get(o, 'firstName'), type: ColumnType.TEXT, width: 80 },
  { label: 'Příjmení', field: 'person.lastName', displayField: 'person', mapper: o => get(o, 'lastName'), type: ColumnType.TEXT, width: 80 },
  { label: 'Rodné Příjmení', field: 'person.maidenName', displayField: 'person', mapper: o => get(o, 'maidenName'), type: ColumnType.TEXT, width: 80 },
  { label: 'Telefon', field: 'person.phone', displayField: 'person', mapper: o => get(o, 'phone'), type: ColumnType.TEXT, width: 80 },
  { label: 'Adresa', field: 'person.address.fullAddress', displayField: 'person', mapper: o => get(o, 'address.fullAddress'), type: ColumnType.TEXT, width: 80 },
  { label: 'Datum narození', field: 'person.birthDate', displayField: 'person', mapper: o => get(o, 'birthDate'), type: ColumnType.DATE, width: 80 },
  { label: 'Rodné číslo', field: 'person.addrbirthNumberess', displayField: 'person', mapper: o => get(o, 'birthNumber'), type: ColumnType.TEXT, width: 80 },
  { label: 'Typ', field: 'type', type: ColumnType.ENUM, enum: participantsTypeEnum, width: 80 },
  { label: 'Název společnosti', field: 'person.companyName', displayField: 'person', mapper: o => get(o, 'companyName'), type: ColumnType.TEXT, width: 80 },
  { label: 'IČ', field: 'person.ic', displayField: 'person', mapper: o => get(o, 'ic'), type: ColumnType.TEXT, width: 80 },
];

// prettier-ignore
export const wantedVehiclesGridColumns = [
  { label: 'Registrační značka', field: 'registrationNumber', type: ColumnType.TEXT, width: 80 },
  { label: 'VIN', field: 'vinNumber', type: ColumnType.TEXT, width: 80 },
  { label: 'Druh', field: 'type', displayField: 'type', type: ColumnType.TEXT, width: 80 },
  { label: 'Barva', field: 'color', displayField: 'color', type: ColumnType.TEXT, width: 80 },
  { label: 'Značka', field: 'brand', displayField: 'brand', type: ColumnType.TEXT, width: 80 },
  { label: 'Jméno držitele', field: 'owner.firstName', displayField: 'owner', mapper: parent => get(parent, 'firstName'), type: ColumnType.TEXT, width: 80 },
  { label: 'Příjmení držitel', field: 'owner.lastName', displayField: 'owner', mapper: parent => get(parent, 'lastName'), type: ColumnType.TEXT, width: 80 }
];

// prettier-ignore
export const weaponsGridColumns = [
  { label: 'Výrobce', field: 'manufacturer', type: ColumnType.TEXT, width: 80 },
  { label: 'Vzor', field: 'pattern', type: ColumnType.TEXT, width: 80 },
  { label: 'Ráže', field: 'caliber', type: ColumnType.TEXT, width: 80 },
  { label: 'Typ zbrojního listu', field: 'licenseType', type: ColumnType.TEXT, width: 80 },
  { label: 'Stav zbrojního listu', field: 'licenseState', type: ColumnType.TEXT, width: 80 },
  { label: 'Jméno držitele', field: 'owner.firstName', displayField: 'owner', mapper: parent => get(parent, 'firstName'), type: ColumnType.TEXT, width: 80 },
  { label: 'Příjmení držitele', field: 'owner.lastName', displayField: 'owner', mapper: parent => get(parent, 'lastName'), type: ColumnType.TEXT, width: 80 }
];

// prettier-ignore
export const dogsGridColumns = [
  { label: 'Jméno psa', field: 'name', type: ColumnType.TEXT, width: 100 },
  { label: 'Rasa', field: 'breed.name', displayField: 'breed', mapper: o => get(o, 'name'), type: ColumnType.TEXT, width: 80 },
  { label: 'Barva', field: 'color.name', displayField: 'color', mapper: o => get(o, 'name'), type: ColumnType.TEXT, width: 80 },
  { label: 'Číslo tetování', field: 'tattoo', displayField: 'tattoo', type: ColumnType.TEXT, width: 110 },
  { label: 'Známka', field: 'idNumber', displayField: 'idNumber', type: ColumnType.TEXT, width: 80 },
  { label: 'Číslo čipu', field: 'chipNumber', displayField: 'chipNumber', type: ColumnType.TEXT, width: 100 },
  { label: 'Zaplacen poplatek v době kontroly', field: 'isFeePaid', displayField: 'isFeePaid', type: ColumnType.TEXT, width: 120 },
];

// prettier-ignore
export const eventsFormHistoriesGridColumns = [
  { label: 'Zaměstnanec', field: 'employee', displayField: 'employee', mapper: parent => get(parent, 'fullName'), type: ColumnType.TEXT, width: 80 },
  { label: 'Akce', field: 'action', type: ColumnType.ENUM, enum: eventHistoryActionEnum, width: 80 },
  { label: 'Zařízení', field: 'source', type: ColumnType.ENUM, enum: eventHistorySourceEnum, width: 100 },
  { label: 'Kategorie', field: 'category', type: ColumnType.ENUM, enum: eventHistoryCategoryEnum, width: 80 },
  { label: 'Datum a Čas', field: 'created', type: ColumnType.DATETIME, width: 100 },
];

// prettier-ignore
export const identificationsGridColumns = [
  { label: 'Typ dokladu', field: 'type', type: ColumnType.ENUM, enum: identificationTypeEnum, width: 80 },
  { label: 'Datum vydání', field: 'issuedOn', type: ColumnType.DATE, width: 200 },
  { label: 'Platnost do', field: 'validTo', type: ColumnType.DATE, width: 200 },
  { label: 'Číslo dokladu', field: 'value', type: ColumnType.TEXT, width: 200 }
];
export const classificationsColumns = [
  {
    label: 'Klasifikace',
    field: 'id',
    type: ColumnType.ENUM,
    enum: participantsClassificationTypeEnum,
    width: 80
  }
];

// prettier-ignore
export const filesGridColumns = [
  { label: 'Název', field: 'name', type: ColumnType.TEXT, width: 80 },
  { label: 'Datum a čas vytvoření', field: 'created', type: ColumnType.DATETIME, width: 100 },
];

// prettier-ignore
export const photosGridColumns = [
  {
    label: 'Název',
    field: 'fileName',
    displayField: 'file',
    mapper: o => get(o, 'name'),
    type: ColumnType.TEXT,
    width: 80,
  },
  {
    label: 'Poznámka',
    field: 'note',
    type: ColumnType.TEXT,
    width: 80
  },
];

// prettier-ignore
export const correspondencesGridColumns = [
  { label: 'Dokument', field: 'id', type: ColumnType.TEXT, width: 80 },
  { label: 'Spis', field: 'record', type: ColumnType.TEXT, width: 80 },
  { label: 'Přílohy', field: 'attachments', type: ColumnType.TEXT, width: 80 },
  { label: 'Číslo jednací', field: 'number', type: ColumnType.TEXT, width: 80 },
];

// prettier-ignore
export const eventsFormClassificationsGridColumns = [
  { label: 'Název', field: 'name', type: ColumnType.TEXT, width: 80 },
];
// prettier-ignore
export const eventsFormMultipleCitizenshipGridColumns = [
  { label: 'Název', field: 'name', type: ColumnType.TEXT, width: 80 },
];

// prettier-ignore
export const eventsFormReportsGridColumns = [
  { label: 'Jméno', field: 'fullName', type: ColumnType.TEXT, width: 80 },
];

export const eventsAudioRecordsColumns = ({ eventId, saveAudioRecordNotify, pbx }) => [
  { label: 'Délka trváni hovoru', field: 'call_time_duration', type: ColumnType.TEXT, width: 80 },
  { label: 'Začátek hovoru', field: 'call_time_start', type: ColumnType.DATETIME, width: 80 },
  {
    label: 'Stahnout',
    field: 'download',
    type: ColumnType.JSX,
    width: 80,
    jsx: withProps({ eventId, saveAudioRecordNotify, pbx })(EventsAudioRecordDownload)
  }
];

// prettier-ignore
export const recordGridColumns = [
  { label: 'Věc dokumentu', field: 'documentCase', type: ColumnType.TEXT, width: 80 },
  { label: 'Spisová značka', field: 'documentRecordSymbol', displayField: 'documentRecordSymbol', type: ColumnType.TEXT, width: 80 },
  { label: 'Přílohy', field: 'attachments', displayField: 'attachments', mapper: parent => parent ? parent.length : 0, type: ColumnType.TEXT, width: 80 },
  { label: 'Číslo jednací', field: 'proceedingNumber', type: ColumnType.TEXT, width: 80 }
];

// prettier-ignore
export const attachmentsGridColumns = [
  { label: 'Název', field: 'file', displayField: 'file', mapper: parent => get(parent, 'name'), type: ColumnType.TEXT, width: 80 },
  { label: 'Čas synchronizace', field: 'syncDatetime', type: ColumnType.DATETIME, width: 80 },
  { label: 'Autor', field: 'author', mapper: parent => get(parent, 'fullName'), type: ColumnType.TEXT, width: 80 },
];



// WEBPACK FOOTER //
// ./src/modules/events/_eventsColumns.tsx